/**
 * HomePage - Redesigned Dashboard
 * 
 * Main dashboard screen with personalized content and AI-powered book recommendations
 */

import React from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, Logo, ChatAvatar } from '../../components/ui';
import { auth } from '../../config/firebase';
import { loggingService } from '../../services/loggingService';
import { PrivateProfileService } from '../../services/privateProfileService';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import { useExploreSections } from '../../hooks/useExploreSections';
import { useMentorChat } from '../../contexts/MentorChatContext';
import { TodayStepCard, TodayStep } from '../../components/dashboard/TodayStepCard';
import { todayStepService } from '../../services/todayStepService';

// Import dashboard sections
import { MentorHero } from '../shared/components';
import MyReadingListSection from './components/MyReadingListSection';
import RecommendedBooksVerticalSection from './components/RecommendedBooksVerticalSection';
import ManageRecommendationsSection from './components/ManageRecommendationsSection';

const HomePage: React.FC = () => {
  const navigation = useNavigation();
  useNavigationLogging('HomePage');
  
  const userId = auth.currentUser?.uid || null;
  const { data, loading, error, refetch } = useExploreSections(userId, 'en');
  const { hasUnreadMessages } = useMentorChat();
  
  const [refreshing, setRefreshing] = React.useState(false);
  const [todayStep, setTodayStep] = React.useState<TodayStep | null>(null);
  const [loadingStep, setLoadingStep] = React.useState(true);
  const [mainFocus, setMainFocus] = React.useState<{
    category: string;
    label: string;
    primaryGoal?: string;
  } | null>(null);

  // Load today's step
  React.useEffect(() => {
    if (userId) {
      todayStepService.getTodayStep(userId).then(step => {
        setTodayStep(step);
        setLoadingStep(false);
      }).catch(err => {
        console.error('Failed to load today\'s step:', err);
        setLoadingStep(false);
      });
    }
  }, [userId]);


  // Load main focus
  React.useEffect(() => {
    if (userId) {
      PrivateProfileService.getMainFocus(userId).then((result: any) => {
        if (result.success && result.data) {
          setMainFocus(result.data);
        }
      }).catch((err: any) => {
        console.error('Failed to load main focus:', err);
      });
    }
  }, [userId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    // Reload today's step and main focus
    if (userId) {
      try {
        const step = await todayStepService.getTodayStep(userId);
        setTodayStep(step);
        
        const focusResult = await PrivateProfileService.getMainFocus(userId);
        if (focusResult.success && focusResult.data) {
          setMainFocus(focusResult.data);
        }
      } catch (err) {
        console.error('Failed to reload dashboard data:', err);
      }
    }
    setRefreshing(false);
  };

  const handleStepStart = () => {
    if (userId && todayStep) {
      // Mark as started in tracking
      todayStepService.markStepStarted(userId, todayStep.insightId, todayStep.bookId);
      
      // Navigate to the full insight so user can read the complete guidance
      if (todayStep.insightId) {
        // @ts-ignore - Navigation types
        navigation.navigate('BookReader', {
          insightIds: [todayStep.insightId],
          languageCode: 'en',
        });
      }
    }
  };

  const handleAskMentorAboutStep = () => {
    if (todayStep) {
      loggingService.logChatOpenFromHome();
      // @ts-ignore - Navigation types
      navigation.navigate('MentorChat', {
        initialMessage: `Can you tell me more about: ${todayStep.outcome}?`,
      });
    }
  };

  const handleChatWithMentor = () => {
    loggingService.logChatOpenFromHome();
    // @ts-ignore - Navigation types
    navigation.navigate('MentorChat');
  };

  const handleBookOfDayPress = () => {
    if (data.bookOfDay) {
      const bookId = data.bookOfDay.book.id.includes('_')
        ? data.bookOfDay.book.id.split('_')[0]
        : data.bookOfDay.book.id;
      // @ts-ignore - Navigation types
      navigation.navigate('BookDetail', {
        bookId,
        languageCode: data.bookOfDay.book.languageCode || 'en',
      });
    }
  };


  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <Logo size={22} />
          <Text style={styles.logoText}>mentorist</Text>
        </View>

        <Pressable 
          onPress={handleChatWithMentor} 
          style={({ pressed }) => [
            styles.chatButton,
            pressed && styles.chatButtonPressed
          ]}
        >
          <Ionicons name="chatbubble-ellipses-outline" size={24} color={Theme.colors.textPrimary} />
          {hasUnreadMessages && <View style={styles.unreadDot} />}
        </Pressable>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={Theme.colors.primary}
          />
        }
      >
        {/* Main Focus Hero Section - Changes via conversation, no manual button */}
        {mainFocus && (
          <View style={styles.mainFocusSection}>
            <View style={styles.mainFocusHeader}>
              <ChatAvatar type="mentor" size="large" />
              <View style={styles.mainFocusTextContainer}>
                <Text variant="caption" style={styles.mainFocusLabel}>YOUR JOURNEY</Text>
                <Text variant="heading2" style={styles.mainFocusTitle}>
                  {mainFocus.label}
                </Text>
                {mainFocus.primaryGoal && (
                  <Text variant="body2" color="secondary" style={styles.mainFocusGoal}>
                    "{mainFocus.primaryGoal}"
                  </Text>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Dashboard sections - redesigned layout with Today's Step as hero */}
        
        {/* 1. TODAY'S STEP - Hero section showing daily action */}
        {!loadingStep && todayStep && (
          <View style={[styles.sectionContainer, styles.todayStepContainer]}>
            <TodayStepCard
              step={todayStep}
              onStart={handleStepStart}
              onAskMentor={handleAskMentorAboutStep}
            />
          </View>
        )}

        {/* 2. My Reading List */}
        <MyReadingListSection />
        
        {/* 3. Recommended Books - Vertical with AI Personalization */}
        <RecommendedBooksVerticalSection />
        
        {/* 4. Free Daily Book */}
        <MentorHero
          book={data.bookOfDay?.book || null}
          bookReason={data.bookOfDay?.reason}
          onMentorPress={handleChatWithMentor}
          onBookPress={handleBookOfDayPress}
          loading={loading || !data.bookOfDay}
        />
        
        {/* 5. Manage Recommendations */}
        <ManageRecommendationsSection />
        
        {/* Bottom spacing */}
        <View style={styles.bottomSpacing} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: Theme.spacing.sm,
    paddingBottom: Theme.spacing.xl,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.lg,
    paddingTop: Theme.spacing.md,
    paddingBottom: Theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Theme.colors.border,
    backgroundColor: Theme.colors.surface,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  logoText: {
    fontSize: 24,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    lineHeight: 32,
  },
  chatButton: {
    padding: 4,
    position: 'relative',
    marginRight: -4, // Align visually with edge
  },
  chatButtonPressed: {
    opacity: 0.7,
  },
  unreadDot: {
    position: 'absolute',
    top: 4,
    right: 2,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FF3B30', // Red
    borderWidth: 1.5,
    borderColor: Theme.colors.surface,
  },
  mainFocusSection: {
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.xl,
    backgroundColor: Theme.colors.surface,
    marginBottom: Theme.spacing.lg,
  },
  mainFocusHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.md,
  },
  mainFocusTextContainer: {
    flex: 1,
  },
  mainFocusLabel: {
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: Theme.colors.textSecondary,
    marginBottom: Theme.spacing.xs,
    fontSize: 11,
    fontWeight: '600',
  },
  mainFocusTitle: {
    marginBottom: Theme.spacing.sm,
  },
  mainFocusGoal: {
    fontStyle: 'italic',
  },
  sectionContainer: {
    paddingHorizontal: Theme.spacing.lg,
  },
  todayStepContainer: {
    marginBottom: Theme.spacing.xxxl * 2, // Much more spacing after Today's Step
  },
  bottomSpacing: {
    height: Theme.spacing.xl,
  },
});

export default HomePage;
